'use client';

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Bus, ArrowLeft, Search, MapPin, Clock, Calendar, AlertTriangle, ChevronRight, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const LOCATIONS = ['Catanduvas', 'Cascavel', 'Três Barras'];

const SCHEDULES = [
  // PG TUR
  {
    company: 'PG TUR',
    color: 'amber',
    note: '⚠️ Atenção: não há operação aos domingos e feriados.',
    routes: [
      {
        origin: 'Cascavel',
        destination: 'Catanduvas',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '11:00', arrival: '--:--', via: 'BR 277' },
              { departure: '18:00', arrival: '--:--', via: 'BR 277' },
            ]
          }
        ]
      },
      {
        origin: 'Catanduvas',
        destination: 'Três Barras',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '12:10', arrival: '--:--', via: 'BR 277' },
            ]
          }
        ]
      },
      {
        origin: 'Três Barras',
        destination: 'Catanduvas',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '15:00', arrival: '--:--', via: 'BR 277' },
            ]
          }
        ]
      },
      {
        origin: 'Catanduvas',
        destination: 'Cascavel',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '06:30', arrival: '--:--', via: 'BR 277' },
              { departure: '16:00', arrival: '--:--', via: 'BR 277' },
            ]
          }
        ]
      }
    ]
  },
  // VIAÇÃO SUDOESTE
  {
    company: 'VIAÇÃO SUDOESTE',
    color: 'emerald',
    note: '⚠️ Atenção: não há operação aos domingos e feriados.',
    routes: [
      {
        origin: 'Cascavel',
        destination: 'Catanduvas',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '14:00', arrival: '--:--', via: 'Fixo' },
            ]
          }
        ]
      },
      {
        origin: 'Catanduvas',
        destination: 'Três Barras',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '15:00', arrival: '--:--', via: 'Fixo' },
            ]
          }
        ]
      },
      {
        origin: 'Três Barras',
        destination: 'Catanduvas',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '10:30', arrival: '--:--', via: 'Fixo' },
            ]
          }
        ]
      },
      {
        origin: 'Catanduvas',
        destination: 'Cascavel',
        periods: [
          {
            days: 'Segunda a sábado',
            times: [
              { departure: '11:30', arrival: '--:--', via: 'Fixo' },
            ]
          }
        ]
      }
    ]
  }
];

export default function BusSchedulePage() {
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');

  const filteredSchedules = useMemo(() => {
    return SCHEDULES.map(company => ({
      ...company,
      routes: company.routes.filter(route => {
        const matchOrigin = !origin || route.origin === origin;
        const matchDestination = !destination || route.destination === destination;
        return matchOrigin && matchDestination;
      })
    })).filter(company => company.routes.length > 0);
  }, [origin, destination]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Horários de Ônibus Catanduvas PR",
    "areaServed": {
      "@type": "Place",
      "name": "Catanduvas, PR"
    },
    "description": "Consulta de horários de transporte intermunicipal entre Catanduvas, Cascavel e Três Barras. Rotas: Catanduvas para Cascavel, Cascavel para Catanduvas, Catanduvas para Três Barras e Três Barras para Catanduvas.",
    "provider": [
      { "@type": "Organization", "name": "PG Tur" },
      { "@type": "Organization", "name": "Viação Sudoeste" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      {/* Header */}
      <header className="bg-blue-700 text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:bg-white/10 px-3 py-2 rounded-xl transition-colors group">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-bold hidden sm:inline">Voltar para o início</span>
          </Link>
          <div className="flex items-center gap-3">
            <Bus className="w-6 h-6 text-blue-200" />
            <h1 className="text-xl font-black uppercase tracking-tighter italic">Catanduvas Ônibus</h1>
          </div>
          <div className="w-10 sm:w-32"></div> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Hero Section */}
        <section className="text-center space-y-4">
          <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tighter uppercase italic">
            Horários de ônibus em Catanduvas PR
          </h1>
          <div className="max-w-2xl mx-auto p-6 bg-white rounded-3xl border border-slate-200 shadow-sm text-slate-600 leading-relaxed">
            <p className="mb-4">
              Informações atualizadas sobre o transporte intermunicipal em nossa cidade. Atualmente, duas empresas operam as principais linhas:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-2xl border border-amber-100">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                <p className="text-sm"><strong>PG Tur:</strong> Horários de segunda a sábado (não opera aos domingos e feriados).</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-2xl border border-emerald-100">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                <p className="text-sm"><strong>Viação Sudoeste:</strong> Horários de segunda a sábado (não opera aos domingos e feriados).</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filtros */}
        <section className="bg-white p-6 rounded-3xl shadow-xl border border-slate-200">
          <div className="flex items-center gap-3 mb-6">
            <Search className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-slate-800">Filtrar Horários</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Origem</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select 
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl appearance-none focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 transition-all"
                >
                  <option value="">Todas as origens</option>
                  {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">Destino</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select 
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl appearance-none focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 transition-all"
                >
                  <option value="">Todos os destinos</option>
                  {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
                </select>
              </div>
            </div>
          </div>
          {(origin || destination) && (
            <button 
              onClick={() => { setOrigin(''); setDestination(''); }}
              className="mt-6 text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
            >
              Limpar filtros
            </button>
          )}
        </section>

        {/* Listagem de Horários */}
        <div className="space-y-12">
          <AnimatePresence mode="wait">
            {filteredSchedules.length > 0 ? (
              filteredSchedules.map((company, idx) => (
                <motion.section 
                  key={company.company}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: idx * 0.1 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl bg-${company.color}-500 flex items-center justify-center shadow-lg shadow-${company.color}-100`}>
                      <Bus className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className={`text-2xl font-black uppercase tracking-tighter italic text-${company.color}-600`}>
                        {company.company}
                      </h2>
                      {company.note && (
                        <p className="text-xs font-bold text-red-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {company.note}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    {company.routes.map((route, rIdx) => (
                      <div key={rIdx} className="bg-white rounded-[2rem] shadow-sm border border-slate-200 overflow-hidden">
                        <div className={`bg-${company.color}-50 px-6 py-4 border-b border-${company.color}-100 flex items-center justify-between`}>
                          <div className="flex items-center gap-3">
                            <span className="font-black text-slate-800 uppercase tracking-tighter">{route.origin}</span>
                            <ChevronRight className={`w-4 h-4 text-${company.color}-400`} />
                            <span className="font-black text-slate-800 uppercase tracking-tighter">{route.destination}</span>
                          </div>
                        </div>
                        
                        <div className="p-6 space-y-8">
                          {route.periods.map((period, pIdx) => (
                            <div key={pIdx} className="space-y-4">
                              <div className="flex items-center gap-2 text-slate-400">
                                <Calendar className="w-4 h-4" />
                                <span className="text-xs font-black uppercase tracking-widest">{period.days}</span>
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {period.times.map((t, tIdx) => (
                                  <div key={tIdx} className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-md transition-all group">
                                    <div className="flex items-center gap-4">
                                      <div className="bg-white p-2 rounded-lg shadow-sm">
                                        <Clock className={`w-4 h-4 text-${company.color}-500`} />
                                      </div>
                                      <div className="flex flex-col w-full">
                                        <div className="flex items-center gap-6 sm:gap-10">
                                          <div className="flex flex-col">
                                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Saída</span>
                                            <span className="font-black text-2xl text-slate-800 tracking-tighter leading-none">{t.departure}</span>
                                          </div>
                                          
                                          <div className="flex items-center pt-3">
                                            <ChevronRight className={`w-5 h-5 text-${company.color}-300`} />
                                          </div>

                                          <div className="flex flex-col">
                                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Chegada</span>
                                            <span className="font-black text-2xl text-slate-800 tracking-tighter leading-none">{t.arrival}</span>
                                          </div>
                                        </div>
                                        <div className="mt-3 flex items-center gap-2">
                                          <span className={`text-[10px] font-bold bg-${company.color}-50 text-${company.color}-600 px-2 py-0.5 rounded-md border border-${company.color}-100 uppercase tracking-widest`}>
                                            Via {t.via}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className={`opacity-0 group-hover:opacity-100 transition-opacity text-${company.color}-500`}>
                                      <Bus className="w-4 h-4" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.section>
              ))
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20 bg-white rounded-[2rem] border border-dashed border-slate-300"
              >
                <div className="bg-slate-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Nenhum horário encontrado</h3>
                <p className="text-slate-500 mt-2">Tente mudar os filtros de origem e destino.</p>
                <button 
                  onClick={() => { setOrigin(''); setDestination(''); }}
                  className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-blue-700 transition-colors"
                >
                  Ver todos os horários
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Info */}
        <section className="bg-slate-800 text-white p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
          <div className="relative z-10 space-y-4">
            <h3 className="text-xl font-black uppercase tracking-tighter italic flex items-center gap-2">
              <Info className="w-6 h-6 text-blue-400" />
              Informações Importantes
            </h3>
            <ul className="space-y-3 text-slate-300 text-sm">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                Os horários podem sofrer alterações sem aviso prévio pelas empresas.
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                Em feriados nacionais, as empresas costumam operar com horários de domingo.
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                Para passagens e dúvidas específicas, procure o guichê na rodoviária.
              </li>
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}
